import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isNil } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { COLORS_MAP } from '../utils/mixed-select-dropdown';

import './mixed-select-dropdown-list.scss';

import type { MixedSelectDropdownItem } from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  items: Array<MixedSelectDropdownItem>,
  withMulti?: boolean,
  withRemovable?: boolean,
  withColors?: boolean,
  withIcons?: boolean,
  withTruncate?: boolean,
  withSmallSize?: boolean,
  withTags?: boolean,
  isDisabled?: boolean,
  onItemsRemove: (a: string) => void
};

class MixedSelectDropdownList extends React.PureComponent<Props> {
  handleItemsRemove = (event: SyntheticEvent<HTMLDivElement>): void => {
    const { onItemsRemove } = this.props;
    const target = event.currentTarget;
    const identifier = target.dataset.identifier || null;

    if (identifier && onItemsRemove) {
      onItemsRemove(identifier);
    }
  };

  renderCross(item: MixedSelectDropdownItem): React.ReactNode {
    const { withMulti, withColors, isDisabled, withSmallSize } = this.props;

    const crossClasses = classnames('mixed-select-dropdown-list__cross', {
      'mixed-select-dropdown-list__cross--with-small-size': Boolean(withSmallSize),
      'mixed-select-dropdown-list__cross--is-disabled': isDisabled
    });

    const iconColor = withMulti && withColors
      ? item.color
      : 'primary';

    return (
      <div
        className={crossClasses}
        data-identifier={item.identifier}
        onClick={this.handleItemsRemove}
      >
        <SVGDynamicIcon
          name="cross-custom-fg"
          width={8}
          height={8}
          customColor={isDisabled ? 'grey' : iconColor}
          clickable
        />
      </div>
    );
  }

  renderPrimaryIcon(item: MixedSelectDropdownItem): React.ReactNode {
    const { isDisabled } = this.props;
    const icon = item.icon?.primary || null;

    if (isNil(icon)) {
      return null;
    }

    return (
      <div className="mixed-select-dropdown-list__icon--primary">
        <SVGDynamicIcon
          name={icon.name}
          width={16}
          height={16}
          customColor={isDisabled ? 'grey' : 'black'}
        />
      </div>
    );
  }

  renderSecondaryTag(item: MixedSelectDropdownItem): React.ReactNode {
    const { withTruncate } = this.props;
    const tag = item.tag?.secondary || null;

    if (isNil(tag)) {
      return null;
    }

    const tagClasses = classnames(
      'mixed-select-dropdown-list__tag',
      'mixed-select-dropdown-list__tag--secondary', {
        'text-overflow-ellipsis': Boolean(withTruncate)
      }
    );

    const labelClasses = classnames({
      'mixed-select-dropdown-list__tag-label': Boolean(withTruncate),
      'text-overflow-ellipsis': Boolean(withTruncate)
    });

    return (
      <div
        className={tagClasses}
        style={{ color: tag?.color, borderColor: tag?.color }}
      >
        <div
          className={labelClasses}
          {...(withTruncate ? { title: tag?.label } : null)}
        >
          {tag?.label}
        </div>
      </div>
    );
  }

  renderColor(item: MixedSelectDropdownItem): React.ReactNode {
    return (
      <div
        className="mixed-select-dropdown-list__color"
        style={{ backgroundColor: item.color }}
      />
    );
  }

  renderLabel(item: MixedSelectDropdownItem, hasSecondaryTag: boolean | undefined): React.ReactNode {
    const label = item.labelWhenSelected || item.label;

    if (hasSecondaryTag) {
      return (
        <div className="mixed-select-dropdown-list__label-inner text-overflow-ellipsis">
          {label}
        </div>
      );
    }

    return label;
  }

  renderItem = (item: MixedSelectDropdownItem): JSX.Element => {
    const {
      withMulti,
      withRemovable,
      withColors,
      withIcons,
      withTruncate,
      withSmallSize,
      withTags,
      isDisabled
    } = this.props;

    const hasPrimaryIcon = withIcons && !withMulti && !isNil(item.icon?.primary);
    const hasMultiColor = withMulti && withColors;
    const hasSecondaryTag = withTags && !withMulti && !withColors && !isNil(item.tag?.secondary);

    const itemClasses = classnames('mixed-select-dropdown-list__item', {
      'mixed-select-dropdown-list__item--is-multi': withMulti,
      'mixed-select-dropdown-list__item--is-disabled': isDisabled,
      'mixed-select-dropdown-list__item--with-small-size': Boolean(withSmallSize),
      [`mixed-select-dropdown-list__item--with-color-${COLORS_MAP[item.color || 'current']}`]: hasMultiColor
    });

    const valueClasses = classnames('mixed-select-dropdown-list__value', {
      'mixed-select-dropdown-list__value--with-primary-icon': hasPrimaryIcon,
      'mixed-select-dropdown-list__value--with-color': !withMulti && withColors,
      'mixed-select-dropdown-list__value--with-color-multi': hasMultiColor,
      'mixed-select-dropdown-list__value--with-small-size': Boolean(withSmallSize)
    });

    const labelClasses = classnames('mixed-select-dropdown-list__label', {
      'text-overflow-ellipsis': withTruncate,
      'mixed-select-dropdown-list__label--with-secondary-tag': hasSecondaryTag,
      [`mixed-select-dropdown-list__label--with-truncate-${withMulti ? 'multi' : 'single'}`]: withTruncate,
      [`mixed-select-dropdown-list__label--with-color-${COLORS_MAP[item.color || 'current']}`]: hasMultiColor
    });

    return (
      <div
        key={`mixed-select-dropdown-list__item-${item.identifier}-${item.value}`}
        className={itemClasses}
        {...(withTruncate ? { title: item.labelWhenSelected || item.label } : null)}
      >
        <div className={valueClasses}>
          {
            hasPrimaryIcon && !withColors &&
              this.renderPrimaryIcon(item)
          }
          {
            withColors && !withMulti && !hasPrimaryIcon &&
              this.renderColor(item)
          }
          <div className={labelClasses}>
            {
              this.renderLabel(item, hasSecondaryTag)
            }
            {
              hasSecondaryTag &&
                this.renderSecondaryTag(item)
            }
          </div>
        </div>
        {
          withMulti && withRemovable &&
            this.renderCross(item)
        }
      </div>
    );
  };

  render(): React.ReactNode {
    const { items } = this.props;

    if (isEmpty(items)) {
      return null;
    }

    return (
      <React.Fragment>
        {items.map(this.renderItem)}
      </React.Fragment>
    );
  }
}

export default MixedSelectDropdownList;
